/**
 * API Client with JWT Authentication
 * 
 * Features:
 * - Automatic JWT token attachment
 * - Token refresh on 401
 * - Request/response logging
 * - Device fingerprint injection
 * - Retry with exponential backoff
 */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_URL, TOKEN_KEY, REFRESH_TOKEN_KEY } from './constants';
import { logger } from './logger';
import { deviceFingerprint } from './fingerprint';
import { useAuthStore } from '@/store/auth';

// Token cache
let accessToken: string | null = null;

/**
 * Get access token from cache or localStorage
 */
export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem(TOKEN_KEY);
  }
  return accessToken;
}

/**
 * Clear cached access token
 */
export function clearAccessToken(): void {
  accessToken = null;
}

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token and device fingerprint
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const startTime = Date.now();
    config.metadata = { startTime };

    // Get token from store
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add device fingerprint
    const fingerprint = await deviceFingerprint.generate();
    config.headers['X-Device-Fingerprint'] = fingerprint;

    // Log request
    logger.apiRequest(
      config.method?.toUpperCase() || 'GET',
      config.url || '',
      {
        params: config.params,
        hasAuth: !!token,
      }
    );

    return config;
  },
  (error: AxiosError) => {
    logger.apiError('REQUEST', error.config?.url || '', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 and refresh token
api.interceptors.response.use(
  (response) => {
    const duration = Date.now() - (response.config.metadata?.startTime || 0);
    
    logger.apiResponse(
      response.config.method?.toUpperCase() || 'GET',
      response.config.url || '',
      response.status,
      duration
    );

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Log error
    logger.apiError(
      originalRequest?.method?.toUpperCase() || 'UNKNOWN',
      originalRequest?.url || '',
      error
    );

    // Handle 401 Unauthorized - Try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        
        if (!refreshToken) {
          // No refresh token, logout
          useAuthStore.getState().logout();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try to refresh token
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        // Update tokens
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        
        useAuthStore.getState().setAuth(
          useAuthStore.getState().user!,
          newToken,
          newRefreshToken
        );

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        logger.error('Token refresh failed', refreshError as Error);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API Methods

/**
 * Authentication
 */
export const authApi = {
  /**
   * Request OTP for phone number
   */
  requestOTP: async (phone: string) => {
    const response = await api.post('/api/auth/send-otp', { phone });
    return response.data;
  },

  /**
   * Verify OTP and get JWT tokens
   */
  verifyOTP: async (phone: string, code: string) => {
    const response = await api.post<{
      success: boolean;
      data: {
        access_token: string;
        expires_in: number;
        user: {
          id: string;
          phone: string;
          name?: string;
          role: string;
        };
      };
    }>('/api/auth/verify-otp', { phone, otp: code });
    return response.data;
  },

  /**
   * Refresh access token (TODO: Not implemented in backend yet)
   */
  refreshToken: async (refreshToken: string) => {
    const response = await api.post<{
      token: string;
      refreshToken: string;
    }>('/api/auth/refresh', { refreshToken });
    return response.data;
  },

  /**
   * Logout
   */
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  },
};

/**
 * Rider API
 */
export const riderApi = {
  /**
   * Get rider profile
   */
  getProfile: async () => {
    const response = await api.get('/api/riders/me');
    return response.data;
  },

  /**
   * Update rider status
   */
  updateStatus: async (status: 'offline' | 'available' | 'busy') => {
    const response = await api.put('/api/riders/me/status', { status });
    return response.data;
  },

  /**
   * Get rider payout batches (Sprint 4G)
   */
  getPayoutBatches: async () => {
    const response = await api.get('/api/payouts/rider/batches');
    return response.data; // Returns { success, data: { batches, count } }
  },

  /**
   * Update rider location
   */
  updateLocation: async (lat: number, lng: number, accuracy: number) => {
    const response = await api.post('/api/gps/track', {
      lat,
      lng,
      accuracy,
      timestamp: new Date().toISOString(),
    });
    return response.data;
  },

  /**
   * Go online (requires zone and GPS)
   */
  goOnline: async (latitude: number, longitude: number) => {
    const response = await api.post('/api/riders/online', {
      latitude,
      longitude,
    });
    return response.data;
  },

  /**
   * Go offline
   */
  goOffline: async () => {
    const response = await api.post('/api/riders/offline');
    return response.data;
  },
};

// Sprint 28 Final Gate: Map backend error codes to user-friendly messages
const ASSIGNMENT_ERROR_MESSAGES: Record<string, string> = {
  ASSIGNMENT_EXPIRED: 'Assignment expired — system reassigned',
  NOT_ASSIGNED_TO_RIDER: 'Assignment no longer available',
  ALREADY_ACCEPTED: 'Order already accepted by another rider',
  ORDER_NOT_FOUND: 'Order not found',
  NOT_AUTHORIZED_FOR_ORDER: 'You are not authorized for this order',
  ISSUE_REPORTED_BLOCK: 'Cannot complete — issue reported, awaiting instructions',
};

/**
 * Extract user-friendly error message from API error
 */
export function getAssignmentErrorMessage(error: any): string {
  const code = error?.response?.data?.error?.code;
  if (code && ASSIGNMENT_ERROR_MESSAGES[code]) {
    return ASSIGNMENT_ERROR_MESSAGES[code];
  }
  return error?.response?.data?.error?.message || error?.message || 'An error occurred';
}

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Get available orders
   */
  getAvailable: async () => {
    const response = await api.get('/api/orders/available');
    return response.data.data.orders; // API wraps in { success, data: { orders } }
  },

  /**
   * Accept assigned order (Sprint 27.3: Smart Assignment)
   * Sprint 28 Final Gate: Handles race condition error codes
   * Falls back to claim-order for backward compatibility
   */
  accept: async (orderId: string) => {
    try {
      // Try new smart assignment endpoint first
      const response = await api.post(`/api/riders/accept-assignment/${orderId}`);
      return response.data;
    } catch (error: any) {
      // If 404, fall back to old claim-order endpoint
      if (error?.response?.status === 404 && !error?.response?.data?.error?.code) {
        const response = await api.post('/api/riders/claim-order', { order_id: orderId });
        return response.data;
      }
      // Re-throw with enhanced message
      const enhancedError = new Error(getAssignmentErrorMessage(error));
      (enhancedError as any).code = error?.response?.data?.error?.code;
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }
  },

  /**
   * Reject assigned order (Sprint 27.3: Smart Assignment)
   * Sprint 28 Final Gate: Handles race condition error codes
   */
  reject: async (orderId: string) => {
    try {
      const response = await api.post(`/api/riders/reject-assignment/${orderId}`);
      return response.data;
    } catch (error: any) {
      // Re-throw with enhanced message
      const enhancedError = new Error(getAssignmentErrorMessage(error));
      (enhancedError as any).code = error?.response?.data?.error?.code;
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }
  },

  /**
   * Update order status
   */
  updateStatus: async (orderId: string, status: string) => {
    const response = await api.put(`/api/riders/orders/${orderId}/status`, { status });
    return response.data;
  },

  /**
   * Upload proof of delivery
   */
  uploadProof: async (orderId: string, photo: File, signature: string) => {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('signature', signature);

    const response = await api.post(`/api/orders/${orderId}/proof`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Sprint 28: Report order issue (facts only)
   * Rider reports what they observe - does NOT decide refunds
   */
  reportIssue: async (params: {
    orderId: string;
    location: 'pickup' | 'dropoff';
    issueType: string;
    notes?: string;
  }) => {
    const response = await api.post(`/api/riders/orders/${params.orderId}/report-issue`, {
      location: params.location,
      issue_type: params.issueType,
      notes: params.notes,
    });
    return response.data;
  },
};

/**
 * Chat API
 */
export const chatApi = {
  /**
   * Get chat history
   */
  getHistory: async (threadId: string) => {
    const response = await api.get(`/api/chat/${threadId}`);
    return response.data;
  },

  /**
   * Send message
   */
  sendMessage: async (threadId: string, message: string) => {
    const response = await api.post(`/api/chat/${threadId}`, { message });
    return response.data;
  },

  /**
   * Get all threads
   */
  getThreads: async () => {
    const response = await api.get('/api/chat/threads');
    return response.data;
  },
};

/**
 * Earnings API
 */
export const earningsApi = {
  /**
   * Get earnings summary
   */
  getSummary: async (period: 'day' | 'week' | 'month') => {
    const response = await api.get('/api/riders/earnings', {
      params: { period },
    });
    return response.data;
  },
};

/**
 * Zones API
 */
export const zonesApi = {
  /**
   * Get all active zones
   */
  getAll: async () => {
    const response = await api.get('/api/zones');
    return response.data.zones; // Backend returns { zones: [...] }
  },
};

// Type augmentation for axios config
declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
    _retry?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

export default api;

